/**
 * What a staged changeset would do to the numbers — PLAN §4.4's *"live preview
 * of how vitals bars move before accepting"*.
 *
 * The whole calculation happens **in the browser, against a copy of the local
 * workspace**. That is not an optimisation: it is what makes the preview honest.
 * The card shows the same engine, on the same document, that the vitals rail
 * behind it is showing — so "OG 1.050 → 1.054" is a promise the editor will keep
 * the instant the user accepts, not a number the server computed against a
 * workspace that has since moved on.
 *
 * Kept pure and separate from the component for the reason `lib/style-bar.ts`
 * is: a preview that is subtly wrong still looks plausible.
 */

import { getStyle } from '@beerwright/catalog';
import type { Changeset, RecipeDoc, WorkspaceState } from '@beerwright/commands';
import { applyChangeset } from '@beerwright/commands';
import type { RecipeCalculation } from '@beerwright/core';
import type { Band, VitalKey } from '@/lib/style-bar';
import { calculationInWorkspace } from '@/store/calculations';

/** One vital, before and after. */
export interface VitalDelta {
  readonly key: VitalKey;
  readonly label: string;
  /** `null` when the proposal creates the recipe — there is no "before". */
  readonly current: number | null;
  readonly proposed: number;
  /** The style's published range, when a style is set. */
  readonly range: Band | null;
  /** Where the proposed value lands relative to `range`. */
  readonly status: 'in-range' | 'below' | 'above' | 'no-range';
  /** True when the proposal moves the value from outside the range to inside it. */
  readonly entersRange: boolean;
  /** True when it does the reverse. */
  readonly leavesRange: boolean;
}

/** Everything a diff card needs to render. */
export type ChangesetPreview =
  | {
      readonly ok: true;
      /** The recipe as it stands, or `null` when the changeset creates it. */
      readonly before: RecipeCalculation | null;
      readonly after: RecipeCalculation;
      readonly deltas: readonly VitalDelta[];
      readonly warnings: readonly string[];
    }
  | {
      readonly ok: false;
      /** Why the selected subset cannot apply — usually an ordering dependency. */
      readonly reason: string;
    };

const VITALS: readonly { key: VitalKey; label: string; read: (c: RecipeCalculation) => number }[] =
  [
    { key: 'og', label: 'OG', read: (c) => c.vitals.og },
    { key: 'fg', label: 'FG', read: (c) => c.vitals.fg },
    { key: 'ibu', label: 'IBU', read: (c) => c.vitals.ibu },
    // "Colour" rather than "SRM": the card prints the scale's own label from the
    // workspace's preference, and a delta that carried a unit in its data would
    // be a second, staler answer to the same question.
    { key: 'srm', label: 'Color', read: (c) => c.vitals.srm },
    { key: 'abv', label: 'ABV', read: (c) => c.vitals.abv },
  ];

/**
 * Build a preview for a changeset, or for a chosen subset of it.
 *
 * @param state - The live workspace. Never mutated.
 * @param changeset - The staged proposal.
 * @param selected - Indices of the changes to include. Omit for all of them.
 * @param recipeId - The recipe to read vitals from; falls back to the changeset's.
 */
export function previewChangeset(
  state: WorkspaceState,
  changeset: Changeset,
  selected: ReadonlySet<number> | null,
  recipeId: string | null,
): ChangesetPreview {
  const changes = changeset.changes.filter((_, index) => selected === null || selected.has(index));
  if (changes.length === 0) {
    return { ok: false, reason: 'Nothing selected.' };
  }

  const applied = applyChangeset(state, { ...changeset, changes });
  if (!applied.ok) {
    // Almost always an ordering dependency: a later command addresses an item an
    // earlier, deselected one created. Say so rather than showing "invalid".
    return {
      ok: false,
      reason:
        selected === null
          ? `These changes no longer apply: ${applied.error.message}`
          : 'These changes depend on each other — accept them together.',
    };
  }

  // A proposal stored before the server checked its target can name a recipe
  // that never existed — the model's label for the one it was creating — so a
  // target that is nowhere falls back to the recipe the changes address.
  const named =
    recipeId !== null && applied.state.recipes[recipeId] !== undefined ? recipeId : null;
  const target = named ?? recipeIdOf(changeset) ?? recipeId;
  if (target === null) {
    return { ok: false, reason: 'This proposal does not name a recipe.' };
  }

  const afterDoc = applied.state.recipes[target];
  if (afterDoc === undefined) {
    return { ok: false, reason: 'That recipe no longer exists.' };
  }
  const after = calculationInWorkspace(applied.state, afterDoc);
  if (after === null) return { ok: false, reason: 'That recipe has no equipment profile.' };

  const beforeDoc = state.recipes[target];
  const before = beforeDoc === undefined ? null : calculationInWorkspace(state, beforeDoc);
  const style = styleOf(afterDoc);

  const deltas = VITALS.map<VitalDelta>((vital) => {
    const range = style?.[vital.key] ?? null;
    const proposed = vital.read(after);
    const current = before === null ? null : vital.read(before);
    return {
      key: vital.key,
      label: vital.label,
      current,
      proposed,
      range,
      status: statusOf(proposed, range),
      entersRange:
        current !== null && range !== null && !inRange(current, range) && inRange(proposed, range),
      leavesRange:
        current !== null && range !== null && inRange(current, range) && !inRange(proposed, range),
    };
  });

  return { ok: true, before, after, deltas, warnings: after.warnings };
}

/** The style's ranges, keyed the way {@link VITALS} reads them. */
function styleOf(doc: RecipeDoc): Partial<Record<VitalKey, Band | null>> | null {
  if (doc.styleId === null) return null;
  const style = getStyle(doc.styleId);
  if (style === undefined) return null;
  return {
    og: style.vitals.og,
    fg: style.vitals.fg,
    ibu: style.vitals.ibu,
    srm: style.vitals.srm,
    abv: style.vitals.abv,
  };
}

function inRange(value: number, range: Band): boolean {
  return value >= range.min && value <= range.max;
}

function statusOf(value: number, range: Band | null): VitalDelta['status'] {
  if (range === null) return 'no-range';
  if (value < range.min) return 'below';
  if (value > range.max) return 'above';
  return 'in-range';
}

/** The recipe every command in a changeset addresses, when they agree on one. */
export function recipeIdOf(changeset: Changeset): string | null {
  const ids = new Set<string>();
  for (const entry of changeset.changes) {
    const payload = entry.command.payload as Record<string, unknown> | null;
    const id = payload === null ? undefined : payload.recipeId;
    if (typeof id === 'string') ids.add(id);
  }
  const [only] = [...ids];
  return ids.size === 1 && only !== undefined ? only : null;
}

/**
 * Whether the workspace has moved since the proposal was staged.
 *
 * Compared on the recipe's own `updatedAt`, which the reducer stamps from the
 * command envelope — so it moves for any edit to that recipe from any device,
 * and does not move for edits to a different one. A stale card is not applied
 * blindly; the user re-checks (PLAN §4.4).
 */
export function isStale(
  state: WorkspaceState,
  recipeId: string | null,
  stagedUpdatedAt: number | null,
): boolean {
  if (recipeId === null || stagedUpdatedAt === null) return false;
  const doc = state.recipes[recipeId];
  if (doc === undefined) return true;
  return doc.updatedAt !== stagedUpdatedAt;
}
